import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiThreadsFilesListQueryKey } from 'src/api/squad'
import { hasPendingResolution } from 'src/components/chat'
import type {
  ApprovalResolution,
  ChatAttachment,
  QuestionResolution,
  TimelineItem
} from 'src/components/chat'

import {
  appendStreamEvent,
  finalAnswerText,
  markApprovalResolved,
  markQuestionResolved
} from './chatTimeline'
import {
  isFileRefusal,
  isQuestionPendingError,
  resumeChat,
  SquadConfigError,
  streamChat,
  streamRun
} from './squadAgent'
import type { SquadStreamEvent } from './squadAgent'
import { cancelRun, getRunStepCount } from './squadApi'
import { isTempId } from './useSquadConversations'
import type { UseSquadConversationsApi } from './useSquadConversations'

type Params = {
  conversations: UseSquadConversationsApi
  configured: boolean
  appToken: string
  agentId: string | null
}

export type ChatSendOptions = {
  startNew?: boolean
  attachments?: ChatAttachment[]
  /** Called once, on the `run` event: Squad accepted the message and bound
   * the files to the run, so the composer may let go of them. */
  onRunAccepted?: () => void
  /** Called when the message was refused before a run started; the message
   * is taken back out of the thread, so the caller restores the draft. */
  onRefused?: () => void
}

export type ChatSession = {
  streaming: boolean
  error: string | null
  send: (prompt: string, options?: ChatSendOptions) => Promise<void>
  resolveApproval: (action: ApprovalResolution) => Promise<void>
  resolveQuestion: (resolution: QuestionResolution) => Promise<void>
  stop: (conversationId?: string | null) => void
  dismissError: () => void
}

type ConsumeResult = { paused: boolean; dropped: boolean }

export const useChatSession = ({
  conversations,
  configured,
  appToken,
  agentId
}: Params): ChatSession => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const abortsRef = useRef<Map<string, AbortController>>(new Map())

  useEffect(() => {
    const aborts = abortsRef.current

    return () => aborts.forEach((controller) => controller.abort())
  }, [])

  const consume = useCallback(
    async (
      stream: AsyncGenerator<SquadStreamEvent, void, void>,
      ctx: {
        conversationId: string
        messageId: string
        onRunAccepted?: () => void
      },
      seed: TimelineItem[]
    ): Promise<ConsumeResult> => {
      let conversationId = ctx.conversationId
      let timeline = seed
      let paused = false
      let sawDone = false
      const persist = () =>
        conversations.updateAssistantMessage(
          conversationId,
          ctx.messageId,
          finalAnswerText(timeline),
          timeline
        )
      const refreshThreadFiles = () =>
        void queryClient.invalidateQueries({
          queryKey: apiThreadsFilesListQueryKey({
            path: { id: conversationId }
          })
        })

      try {
        for await (const event of stream) {
          switch (event.type) {
            case 'run': {
              if (isTempId(conversationId) && event.threadId) {
                conversations.commitThreadId(conversationId, event.threadId)
                conversationId = event.threadId
                ctx.conversationId = event.threadId
              }
              if (event.runId) {
                conversations.setAssistantRunId(
                  conversationId,
                  ctx.messageId,
                  event.runId
                )
              }
              const acceptRun = ctx.onRunAccepted
              ctx.onRunAccepted = undefined
              acceptRun?.()
              refreshThreadFiles()
              break
            }

            case 'file': {
              conversations.addAssistantAttachment(
                conversationId,
                ctx.messageId,
                event.attachment
              )
              refreshThreadFiles()
              break
            }

            case 'token':
            case 'reasoning':
            case 'tool_call':
            case 'tool_result': {
              timeline = appendStreamEvent(timeline, event)
              persist()
              break
            }

            case 'approval_required': {
              timeline = appendStreamEvent(timeline, event)
              paused = true
              persist()
              break
            }

            case 'error':
              throw new Error(event.error)

            case 'paused':
              paused = true
              break

            case 'done':
              sawDone = true
              break

            default:
              break
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          throw err
        }
        if (!finalAnswerText(timeline).trim() && !paused) {
          timeline = appendStreamEvent(timeline, {
            type: 'token',
            text: t('workspaceHome.errors.stopped')
          })
        }
        persist()

        return { paused, dropped: false }
      }

      if (!paused && timeline.length === 0) {
        timeline = appendStreamEvent(timeline, {
          type: 'token',
          text: t('workspaceHome.errors.empty')
        })
      }
      persist()
      refreshThreadFiles()

      return { paused, dropped: !paused && !sawDone }
    },
    [conversations, t, queryClient]
  )

  const reportFailure = useCallback(
    (err: unknown) => {
      if (err instanceof SquadConfigError) {
        setError(t('workspaceHome.errors.notConfigured'))

        return
      }
      if (isQuestionPendingError(err)) {
        setError(t('workspaceHome.errors.questionPending'))

        return
      }
      if (isFileRefusal(err)) {
        setError(t('workspaceHome.attachments.sendRefused'))

        return
      }
      setError(
        err instanceof Error ? err.message : t('workspaceHome.errors.generic')
      )
    },
    [t]
  )

  const send = useCallback(
    async (rawPrompt: string, options?: ChatSendOptions) => {
      const prompt = rawPrompt.trim()
      const attachments = options?.attachments
      if (!prompt && !attachments?.length) return

      const targetId = options?.startNew ? null : conversations.activeId
      const targetConversation = targetId
        ? conversations.conversations.find((c) => c.id === targetId)
        : null
      const threadBusy =
        targetConversation &&
        (targetConversation.messages.some((m) => m.live) ||
          hasPendingResolution(targetConversation))
      if (threadBusy) {
        options?.onRefused?.()

        return
      }

      if (!configured || !agentId || !appToken) {
        options?.onRefused?.()
        setError(t('workspaceHome.errors.notConfigured'))

        return
      }

      setError(null)

      const conversationId =
        targetId ?? conversations.startConversation(prompt, attachments).id
      const userMessage = targetId
        ? conversations.appendUserMessage(targetId, prompt, attachments)
        : null

      const threadId = isTempId(conversationId) ? undefined : conversationId
      const assistant = conversations.appendAssistantMessage(conversationId)
      let runAccepted = false
      let conversationDropped = false
      const ctx = {
        conversationId,
        messageId: assistant.id,
        onRunAccepted: () => {
          runAccepted = true
          options?.onRunAccepted?.()
        }
      }

      const controller = new AbortController()
      abortsRef.current.set(assistant.id, controller)

      try {
        await consume(
          streamChat({
            appToken,
            agentId,
            message: prompt,
            threadId,
            fileIds: attachments?.map((attachment) => attachment.id),
            signal: controller.signal
          }),
          ctx,
          []
        )
      } catch (err) {
        if (!runAccepted && !userMessage) {
          // A brand-new chat the server never accepted: drop it entirely
          // (it only exists locally under a temp id).
          conversationDropped = true
          conversations.deleteConversation(conversationId)
        } else {
          conversations.removeMessage(ctx.conversationId, assistant.id)
          if (!runAccepted && userMessage) {
            conversations.removeMessage(conversationId, userMessage.id)
          }
        }
        if (!runAccepted) options?.onRefused?.()
        reportFailure(err)
      } finally {
        abortsRef.current.delete(assistant.id)
        if (!conversationDropped) {
          conversations.setAssistantLive(
            ctx.conversationId,
            assistant.id,
            false
          )
        }
      }
    },
    [conversations, configured, agentId, appToken, consume, t, reportFailure]
  )

  const reconnect = useCallback(
    async (
      conversationId: string,
      messageId: string,
      runId: string,
      seed: TimelineItem[]
    ) => {
      if (!appToken) return

      const controller = new AbortController()
      abortsRef.current.set(messageId, controller)
      conversations.setAssistantLive(conversationId, messageId, true)
      let handedOffToPolling = false

      try {
        const since = await getRunStepCount(appToken, runId)
        const result = await consume(
          streamRun({ appToken, runId, since, signal: controller.signal }),
          { conversationId, messageId },
          seed
        )
        if (result.dropped) {
          handedOffToPolling = true
          conversations.startRunPolling(conversationId)
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        handedOffToPolling = true
        conversations.startRunPolling(conversationId)
      } finally {
        abortsRef.current.delete(messageId)
        if (!handedOffToPolling) {
          conversations.setAssistantLive(conversationId, messageId, false)
        }
      }
    },
    [appToken, conversations, consume]
  )

  const reconnectRef = useRef(reconnect)
  useEffect(() => {
    reconnectRef.current = reconnect
  })
  const reconnectingRuns = useRef<Set<string>>(new Set())

  const { reconnectRequests, clearReconnectRequest } = conversations

  useEffect(() => {
    for (const [conversationId, req] of Object.entries(reconnectRequests)) {
      if (reconnectingRuns.current.has(req.runId)) continue
      reconnectingRuns.current.add(req.runId)
      clearReconnectRequest(conversationId)
      void reconnectRef
        .current(conversationId, req.messageId, req.runId, req.seed)
        .finally(() => reconnectingRuns.current.delete(req.runId))
    }
  }, [reconnectRequests, clearReconnectRequest])

  const resolveApproval = useCallback(
    async (action: ApprovalResolution) => {
      const conv = conversations.conversations.find(
        (c) => c.id === conversations.activeId
      )
      const msg = conv?.messages.find((m) =>
        m.timeline?.some((i) => i.kind === 'approval' && i.status === 'pending')
      )
      const item = msg?.timeline?.find(
        (i) => i.kind === 'approval' && i.status === 'pending'
      )
      if (!conv || !msg || !item || item.kind !== 'approval') return
      if (conv.messages.some((m) => m.live)) return
      if (!appToken) {
        setError(t('workspaceHome.errors.notConfigured'))

        return
      }

      setError(null)

      const pending = {
        conversationId: conv.id,
        messageId: msg.id,
        approvalId: item.approvalId
      }
      const seed = markApprovalResolved(
        msg.timeline ?? [],
        pending.approvalId,
        action
      )
      conversations.updateAssistantMessage(
        pending.conversationId,
        pending.messageId,
        finalAnswerText(seed),
        seed
      )

      conversations.setAssistantLive(
        pending.conversationId,
        pending.messageId,
        true
      )
      const controller = new AbortController()
      abortsRef.current.set(pending.messageId, controller)

      try {
        await consume(
          resumeChat({
            appToken,
            approvalId: pending.approvalId,
            action,
            signal: controller.signal
          }),
          {
            conversationId: pending.conversationId,
            messageId: pending.messageId
          },
          seed
        )
      } catch (err) {
        reportFailure(err)
      } finally {
        abortsRef.current.delete(pending.messageId)
        conversations.setAssistantLive(
          pending.conversationId,
          pending.messageId,
          false
        )
      }
    },
    [conversations, appToken, consume, t, reportFailure]
  )

  const resolveQuestion = useCallback(
    async (resolution: QuestionResolution) => {
      const conv = conversations.conversations.find(
        (c) => c.id === conversations.activeId
      )
      const msg = conv?.messages.find((m) =>
        m.timeline?.some((i) => i.kind === 'question' && i.status === 'pending')
      )
      const item = msg?.timeline?.find(
        (i) => i.kind === 'question' && i.status === 'pending'
      )
      if (!conv || !msg || !item || item.kind !== 'question') return
      if (conv.messages.some((m) => m.live)) return
      if (!appToken) {
        setError(t('workspaceHome.errors.notConfigured'))

        return
      }

      setError(null)

      const pending = {
        conversationId: conv.id,
        messageId: msg.id,
        approvalId: item.approvalId
      }
      const seed = markQuestionResolved(
        msg.timeline ?? [],
        pending.approvalId,
        resolution
      )
      conversations.updateAssistantMessage(
        pending.conversationId,
        pending.messageId,
        finalAnswerText(seed),
        seed
      )

      conversations.setAssistantLive(
        pending.conversationId,
        pending.messageId,
        true
      )
      const controller = new AbortController()
      abortsRef.current.set(pending.messageId, controller)

      try {
        await consume(
          resumeChat({
            appToken,
            approvalId: pending.approvalId,
            action: 'answer',
            overrides: {
              selected: resolution.selected,
              other: resolution.other ?? ''
            },
            signal: controller.signal
          }),
          {
            conversationId: pending.conversationId,
            messageId: pending.messageId
          },
          seed
        )
      } catch (err) {
        reportFailure(err)
      } finally {
        abortsRef.current.delete(pending.messageId)
        conversations.setAssistantLive(
          pending.conversationId,
          pending.messageId,
          false
        )
      }
    },
    [conversations, appToken, consume, t, reportFailure]
  )

  const stop = useCallback(
    (conversationId?: string | null) => {
      const cancel = (runId?: string) => {
        if (runId && appToken)
          void cancelRun(appToken, runId).catch(() => undefined)
      }

      const targetId = conversationId ?? conversations.activeId
      if (!targetId) {
        abortsRef.current.forEach((controller) => controller.abort())

        return
      }
      const conv = conversations.conversations.find((c) => c.id === targetId)
      conv?.messages.forEach((m) => {
        if (m.live) {
          abortsRef.current.get(m.id)?.abort()
          cancel(m.runId)
        }
      })
      conversations.stopRunPolling(targetId)
      conversations.clearReconnectRequest(targetId)
    },
    [conversations, appToken]
  )

  const dismissError = useCallback(() => setError(null), [])

  const activeConversation = conversations.conversations.find(
    (c) => c.id === conversations.activeId
  )
  const streaming = Boolean(activeConversation?.messages.some((m) => m.live))

  return useMemo(
    () => ({
      streaming,
      error,
      send,
      resolveApproval,
      resolveQuestion,
      stop,
      dismissError
    }),
    [
      streaming,
      error,
      send,
      resolveApproval,
      resolveQuestion,
      stop,
      dismissError
    ]
  )
}
